import posthog from 'posthog-js';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export type ErrorSource = 
  | 'assessment_submission'
  | 'checkout_session'
  | 'payment_processing'
  | 'api_endpoint'
  | 'form_validation'
  | 'network_request'
  | 'react_component'
  | 'state_management'
  | 'third_party_integration'
  | 'unknown';

interface ErrorContext {
  source: ErrorSource;
  severity?: ErrorSeverity;
  assessmentType?: string;
  assessmentId?: string;
  userEmail?: string;
  userId?: string;
  step?: string | number;
  questionId?: string;
  apiEndpoint?: string;
  httpStatus?: number;
  additionalData?: Record<string, unknown>;
}

/**
 * Captures an exception and sends it to PostHog with rich context
 * This is the main function to use throughout the app for error tracking
 */
export function captureException(
  error: Error | unknown,
  context: ErrorContext
) {
  // Extract error details
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorName = error instanceof Error ? error.name : 'UnknownError';
  const errorStack = error instanceof Error ? error.stack : undefined;

  // Determine severity if not provided
  const severity = context.severity || determineSeverity(context.source);

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('[PostHog Error Tracking]', {
      error,
      context,
      severity,
      message: errorMessage,
      stack: errorStack
    });
  }

  // Send to PostHog using the built-in exception tracking
  try {
    // PostHog automatically captures browser metadata ($browser, $os, $lib, etc.)
    // We only need to add custom business-specific properties
    const customProperties: Record<string, unknown> = {
      // Error context
      error_severity: severity,
      error_source: context.source,
      
      // Assessment context (business-specific)
      assessment_type: context.assessmentType,
      assessment_id: context.assessmentId,
      user_email: context.userEmail,
      user_id: context.userId,
      current_step: context.step,
      question_id: context.questionId,
      
      // API context
      api_endpoint: context.apiEndpoint,
      http_status: context.httpStatus,
      
      // Additional custom data
      timestamp: new Date().toISOString(),
      ...context.additionalData,
    };
    
    // Remove undefined values to keep payload clean
    Object.keys(customProperties).forEach(key => {
      if (customProperties[key] === undefined) {
        delete customProperties[key];
      }
    });
    
    // PostHog's captureException automatically includes:
    // - $browser, $os, $lib, $device_type
    // - $current_url, $host, $pathname  
    // - $screen_height, $screen_width
    // - $viewport_height, $viewport_width
    // - Stack trace and error details
    posthog.captureException(
      error instanceof Error ? error : new Error(errorMessage),
      customProperties
    );
    
    // For critical errors, also send a separate high-priority event
    if (severity === 'critical') {
      posthog.capture('critical_error', {
        error_message: errorMessage,
        error_type: errorName,
        error_severity: severity,
        assessment_type: context.assessmentType,
        user_email: context.userEmail,
        source: context.source,
        alert_required: true,
        ...context.additionalData,
      });
    }
  } catch (trackingError) {
    // Fail silently if PostHog itself has issues
    console.error('Failed to send error to PostHog:', trackingError);
  }
}

/**
 * Determines severity based on error source
 */
function determineSeverity(source: ErrorSource): ErrorSeverity {
  switch (source) {
    case 'payment_processing':
    case 'checkout_session':
      return 'critical'; // Revenue impacting
    case 'assessment_submission':
    case 'api_endpoint':
      return 'high';
    case 'form_validation':
    case 'network_request':
      return 'medium';
    case 'state_management':
    case 'third_party_integration':
      return 'low';
    default:
      return 'medium';
  }
}

/**
 * Wrapper for async functions with automatic error tracking
 */
export async function withErrorTracking<T>(
  fn: () => Promise<T>,
  context: ErrorContext
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    captureException(error, context);
    throw error; // Re-throw to maintain original behavior
  }
}

/**
 * Track API errors specifically
 */
export function captureApiError(
  endpoint: string,
  status: number,
  error: Error | unknown,
  additionalContext?: Partial<ErrorContext>
) {
  captureException(error, {
    source: 'api_endpoint',
    apiEndpoint: endpoint,
    httpStatus: status,
    severity: status >= 500 ? 'high' : 'medium',
    ...additionalContext,
  });
}

/**
 * Track form validation errors
 */
export function captureFormError(
  formName: string,
  fieldName: string,
  error: string,
  additionalContext?: Partial<ErrorContext>
) {
  captureException(new Error(`Form validation failed: ${error}`), {
    source: 'form_validation',
    severity: 'low',
    additionalData: {
      form_name: formName,
      field_name: fieldName,
      validation_error: error,
    },
    ...additionalContext,
  });
}

/**
 * Track checkout/payment errors (critical for revenue)
 */
export function captureCheckoutError(
  error: Error | unknown,
  step: 'session_creation' | 'payment_processing' | 'discount_application',
  additionalContext?: Partial<ErrorContext>
) {
  captureException(error, {
    source: step === 'payment_processing' ? 'payment_processing' : 'checkout_session',
    severity: 'critical',
    additionalData: {
      checkout_step: step,
      revenue_impact: true,
    },
    ...additionalContext,
  });
}
